import React, { useCallback, useState } from "react";
import {
  Listbox,
  Transition,
  Menu,
  Dialog,
  TransitionChild,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  MenuButton,
  MenuItems,
  MenuItem,
  DialogPanel,
  DialogBackdrop,
} from "@headlessui/react";
import {
  BellIcon,
  CheckIcon,
  SelectorIcon,
  BadgeCheckIcon,
} from "@heroicons/react/solid";
import {
  BellIcon as BellIconOutline,
  BriefcaseIcon,
  CashIcon,
  ChevronDownIcon,
  CogIcon,
  DeviceMobileIcon,
  LogoutIcon,
  MenuIcon,
  OfficeBuildingIcon,
  ReceiptTaxIcon,
  TagIcon,
  XIcon,
} from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  setLanguage,
  useAuthState,
  useAuthDispatch,
  logout,
  useNotificationState,
  requestUpdateNotification,
  useNotificationDispatch,
  requestGetPointBalance,
  usePointDispatch,
  usePointState,
} from "../../Context";
import { useSettingsState, useSettingsDispatch } from "../../Context";
import { useLocation, useNavigate } from "react-router-dom";
import PajakindLogo from "../PajakindLogo";
import {
  classNames,
  getInitials,
  membershipName,
  notificationType,
  pathBuilder,
} from "../../Utils";
import { clearAuthData } from "../../Utils/AuthUtils";
import "./index.css";
import { ErrorList } from "../ErrorList";
import { EmptyList } from "../EmptyList";
import { useTitle } from "../../Hooks/useTitle";
import DownloadApp from "../DownloadApp/desktop";
import DownloadAppMobile from "../DownloadApp/mobile";
import PointIcon from "../PointIcon";

const languages = [
  { value: "id", label: "Bahasa Indonesia" },
  { value: "en", label: "English" },
];

const GUEST_NAVIGATIONS = [
  { label: "About", path: "https://pajakind.com" },
  { label: "Blog", path: "https://blog.pajakind.com" },
];

const NAVIGATIONS = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Consultation", path: "/consultation?status=ALL" },
  { label: "E-Billing", path: "/ebilling" },
  { label: "E-Meterai", path: "/emeterai/history?status=ALL" },
  { label: "Learn", path: "/learn" },
];

const Header = ({ title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const authDispatch = useAuthDispatch();
  const { user, getUserLoading } = useAuthState();

  const pointDispatch = usePointDispatch();
  const { loading: pointLoading, balance } = usePointState();

  const { language } = useSettingsState();
  const settingsDispatch = useSettingsDispatch();

  const notificationDispatch = useNotificationDispatch();
  const {
    notifications,
    loading,
    error: notificationError,
  } = useNotificationState();
  const unreadNotifications = notifications.filter(
    (notification) => !notification.isRead
  );

  const [menuOpen, setmenuOpen] = useState(false);
  const [isDownloadAppOpen, setDownloadAppOpen] = useState(false);
  const [isDownloadApp, setDownloadApp] = useState(true);
  const [imageError, setimageError] = useState(false);

  const onPointLoad = useCallback(() => {
    requestGetPointBalance(pointDispatch);
  }, [pointDispatch]);

  const handleImageError = () => {
    setimageError(true);
  };

  const handleImageLoad = () => {
    setimageError(false);
  };

  const isDashboard = location.pathname === "/dashboard";

  useTitle(t(title));

  function changeLanguage(language) {
    i18n.changeLanguage(language, (err) => {
      if (!err) setLanguage(settingsDispatch, { language });
    });
  }

  function handleLogout() {
    logout(authDispatch);
  }

  const name = user.name || user.given_name || user.email || "User";
  const initials = getInitials(name);

  function renderLanguageSelect() {
    return (
      <div className="hidden sm:block sm:w-auto md:w-24 lg:w-auto">
        <Listbox value={language} onChange={changeLanguage}>
          <div className="relative">
            <ListboxButton className="relative w-full cursor-default rounded-lg bg-white border border-gray-200 py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 sm:text-sm">
              <span className="block truncate">{t(language)}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <SelectorIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </ListboxButton>
            <Transition
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions className="absolute mt-1 max-h-60 w-fit overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {languages.map((language) => (
                  <ListboxOption
                    key={language.value}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                      }`
                    }
                    value={language.value}
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {language.label}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        </Listbox>
      </div>
    );
  }

  return (
    <div>
      <Transition show={menuOpen}>
        <Dialog
          as="div"
          className="fixed z-40 inset-0 overflow-hidden md:hidden"
          onClose={setmenuOpen}
        >
          <div className="absolute inset-0 overflow-hidden">
            <TransitionChild
              enter="ease-in-out duration-500"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-500"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop
                transition
                className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              />
            </TransitionChild>
            {/* Headless UI marks the backdrop aria-hidden, so the panel cannot
                live inside it. It used to fade with the backdrop; the opacity
                steps below keep that. */}
            <DialogPanel className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <TransitionChild
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full opacity-0"
                enterTo="translate-x-0 opacity-100"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0 opacity-100"
                leaveTo="translate-x-full opacity-0"
              >
                <div className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    <nav className="divide-y divide-slate-900/10 text-base leading-7 text-slate-900">
                      <div className="flex justify-between py-6 px-8">
                        <a className="block w-9 overflow-hidden" href="/">
                          <span className="sr-only">PajakInd Web</span>
                          <PajakindLogo
                            className="h-6 w-auto"
                            aria-hidden="true"
                            viewBox="0 0 160 24"
                            fill="none"
                          />
                        </a>
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          onClick={() => setmenuOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                      <div className="py-6 px-8">
                        <div className="-my-2 items-start space-y-2">
                          {user.username
                            ? NAVIGATIONS.map((m) => (
                                <a
                                  key={m.path}
                                  className="block w-full py-2 font-semibold"
                                  href={m.path}
                                >
                                  {t(m.label)}
                                </a>
                              ))
                            : GUEST_NAVIGATIONS.map((m) => (
                                <a
                                  key={m.path}
                                  className="block w-full py-2 font-semibold"
                                  href={m.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t(m.label)}
                                </a>
                              ))}
                        </div>
                      </div>
                    </nav>
                  </div>
                </div>
              </TransitionChild>
            </DialogPanel>
          </div>
        </Dialog>
      </Transition>
      <header className="sticky inset-x-0 top-0 z-40">
        <div className="sm:bg-gray-100">
          <div className="relative max-w-screen-xl mx-auto">
            {isDownloadApp && isDashboard && (
              <DownloadAppMobile setDownloadApp={setDownloadApp} />
            )}
            <div className="hidden sm:flex h-8 px-8 bg-gray-100 text-gray-600 w-full">
              <div
                className="flex gap-2 text-xs items-center hover:text-blue-500"
                onClick={() => setDownloadAppOpen(true)}
                onMouseOver={() => setDownloadAppOpen(true)}
                onMouseOut={() => setDownloadAppOpen(false)}
              >
                <DeviceMobileIcon className="w-4" />
                <a
                  href="https://s.id/pajakind"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("Download PajakInd App")}
                </a>
              </div>
            </div>
            {isDownloadAppOpen && (
              <div className="absolute left-8 top-8">
                <DownloadApp
                  onMouseOver={() => setDownloadAppOpen(true)}
                  onMouseOut={() => setDownloadAppOpen(false)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="bg-white border-b-2 border-gray-200">
          <div className="flex items-center max-w-screen-xl h-16 gap-8 px-4 mx-auto sm:px-6 lg:px-8">
            <a className="block text-blue-600" href="/">
              <span className="sr-only">PajakInd Web</span>
              <PajakindLogo />
            </a>

            <div className="flex items-center justify-end flex-1 md:justify-between">
              <nav
                className="hidden md:block"
                aria-labelledby="header-navigation"
              >
                <h2 className="sr-only" id="header-navigation">
                  Header navigation
                </h2>

                <ul className="flex items-center gap-6 text-sm">
                  {user.username
                    ? NAVIGATIONS.map((m) => (
                        <li key={m.path}>
                          <a
                            className={`${
                              location.pathname === new URL(m.path, window.location.origin).pathname
                                ? "bg-blue-500/10 rounded-md px-3 py-2 text-blue-700 font-medium"
                                : "text-gray-500 transition hover:text-blue-500"
                            }`}
                            href={m.path}
                          >
                            {t(m.label)}
                          </a>
                        </li>
                      ))
                    : GUEST_NAVIGATIONS.map((m) => (
                        <li key={m.path}>
                          <a
                            className="text-gray-500 transition hover:text-gray-500/75"
                            href={m.path}
                          >
                            {t(m.label)}
                          </a>
                        </li>
                      ))}
                </ul>
              </nav>

              <div className="flex items-center gap-4">
                {user.username ? (
                  <div className="gap-4 flex items-center">
                    {renderLanguageSelect()}

                    <div className="group">
                      <div className="block relative shrink-0 p-2 rounded-md text-gray-600 group-hover:bg-slate-100">
                        {unreadNotifications.length > 0 ? (
                          <BellIcon className="w-5 h-5 text-red-500" />
                        ) : (
                          <BellIconOutline className="w-5 h-5" />
                        )}
                      </div>
                      <div className="relative hidden z-10 group-hover:block">
                        <div className="absolute top-0 -right-24 w-56 pt-2">
                          <div className="bg-white shadow-md rounded-md border border-slate-100 text-xs text-gray-700">
                            {loading ? (
                              <div className="p-4 flex justify-center text-center">
                                <svg
                                  role="status"
                                  className="w-5 h-5 animate-spin text-blue-500"
                                  viewBox="0 0 100 101"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="#E5E7EB"
                                  />
                                  <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </div>
                            ) : (
                              <div>
                                {notifications.length > 0 ? (
                                  <div className="overflow-y-scroll max-h-96">
                                    {notifications.map(
                                      (notification, index) => {
                                        let type =
                                          notification.type || "notification";
                                        type = type.toLowerCase();
                                        const path = pathBuilder(
                                          notification.type,
                                          notification.typeId
                                        );
                                        const Icon =
                                          notificationType[type]?.icon ||
                                          BellIcon;

                                        return (
                                          <a
                                            key={notification.id}
                                            href={path || "#"}
                                            onClick={async (e) => {
                                              e.preventDefault();
                                              if (!notification.isRead) {
                                                await requestUpdateNotification(
                                                  notificationDispatch,
                                                  {
                                                    id: notification.id,
                                                    isRead: true,
                                                  }
                                                );
                                              }
                                              if (path) {
                                                navigate(path);
                                              }
                                            }}
                                          >
                                            <div
                                              key={notification.id}
                                              className={classNames(
                                                index === 0
                                                  ? "rounded-t-md"
                                                  : "rounded-t-none",
                                                notification.isRead
                                                  ? "bg-white"
                                                  : "bg-blue-100",
                                                "p-4 hover:bg-slate-100"
                                              )}
                                            >
                                              <div className="flex items-center text-gray-500 mb-2 notification-type">
                                                <Icon
                                                  className={classNames(
                                                    notificationType[type]
                                                      ?.color ||
                                                      "text-blue-500",
                                                    "h-4 w-4 mr-1"
                                                  )}
                                                />
                                                <span className="mr-1">
                                                  {t(
                                                    notificationType[type]
                                                      ?.title
                                                  ) || t(type)}
                                                </span>
                                                <span>•</span>
                                                <span className="ml-1">
                                                  {dayjs(notification.createdAt)
                                                    .locale(i18n.language)
                                                    .format("DD MMM YYYY")}
                                                </span>
                                              </div>
                                              <p className="font-bold">
                                                {notification.title}
                                              </p>

                                              <p className="line-clamp-2">
                                                {notification.description}
                                              </p>
                                            </div>
                                          </a>
                                        );
                                      }
                                    )}
                                  </div>
                                ) : (
                                  <div className="p-4 text-center">
                                    {notificationError ? (
                                      <ErrorList
                                        className="text-xs"
                                        title="Error"
                                        message={
                                          notificationError?.message ||
                                          "Error fetching notifications."
                                        }
                                        errors={notificationError?.errors}
                                      />
                                    ) : (
                                      <EmptyList
                                        className="text-xs"
                                        title={t("No Notifications")}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="flex justify-center shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                              <a
                                href="/notification"
                                className="p-2 text-center text-xs font-medium text-blue-500"
                              >
                                {t("See All")}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <span
                      aria-hidden="true"
                      className="block w-px h-6 bg-gray-200 rounded-full"
                    ></span>

                    <Menu as="div" className="relative inline-block text-left">
                      {({ open }) => (
                        <>
                          <div>
                            {getUserLoading ? (
                              <div className="flex animate-pulse items-center w-full rounded-lg shrink-0">
                                <div className="w-9 h-9 rounded-full bg-gray-300"></div>
                                <div className="flex-col gap-1 hidden lg:flex">
                                  <div className="ml-2 h-3 w-36 rounded-md bg-gray-300"></div>
                                  <div className="ml-2 h-3 w-36 rounded-md bg-gray-300"></div>
                                </div>
                              </div>
                            ) : (
                              <MenuButton className="flex z-10 items-center transition rounded-lg group shrink-0">
                                {user.imageUrl && !imageError ? (
                                  <img
                                    src={user.imageUrl}
                                    alt="Profile"
                                    className="rounded-full object-cover w-9 h-9"
                                    onError={handleImageError}
                                    onLoad={handleImageLoad}
                                  />
                                ) : (
                                  <div className="inline-flex items-center justify-center w-9 h-9 text-sm text-white bg-blue-500 rounded-full">
                                    {initials}
                                  </div>
                                )}
                                <p className="hidden ml-2 text-xs text-left lg:block">
                                  <strong className="block font-medium">
                                    {name}
                                  </strong>
                                  {user.membership ? (
                                    <span className="text-gray-500">
                                      {t(
                                        membershipName[user.membership] ||
                                          user.membership
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-gray-500">
                                      {user.email || user.phone}
                                    </span>
                                  )}
                                </p>

                                <ChevronDownIcon className="hidden w-5 h-5 ml-4 text-gray-500 transition sm:block group-hover:text-gray-700" />
                              </MenuButton>
                            )}
                          </div>
                          <Transition
                            show={open}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                            afterEnter={onPointLoad}
                          >
                            <MenuItems className="absolute right-0 mt-2 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <div className="px-1 py-1 divide-y divide-gray-100">
                            <div>
                              <MenuItem>
                                {({ focus }) => (
                                  <a
                                    href="/points"
                                    className={`${
                                      focus
                                        ? "bg-blue-500 text-white"
                                        : "text-gray-900"
                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                  >
                                    <PointIcon
                                      containerSize="mr-2 h-5 w-5"
                                      iconSize="h-3 w-3"
                                    />
                                    {pointLoading ? (
                                      <div className="flex animate-pulse items-center w-full rounded-lg shrink-0">
                                        <div className="my-1 h-3 w-16 rounded-md bg-gray-300"></div>
                                      </div>
                                    ) : (
                                      <span>
                                        {(balance.balance || 0).toLocaleString(
                                          "id-ID"
                                        )}{" "}
                                        pts
                                      </span>
                                    )}
                                  </a>
                                )}
                              </MenuItem>
                              <MenuItem>
                                {({ focus }) => (
                                  <a
                                    href="/voucher"
                                    className={`${
                                      focus
                                        ? "bg-blue-500 text-white"
                                        : "text-gray-900"
                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                  >
                                    <ReceiptTaxIcon className="mr-2 h-5 w-5" />
                                    <span className="mr-2">{t("Voucher")}</span>
                                  </a>
                                )}
                              </MenuItem>
                              <MenuItem>
                                {({ focus }) => (
                                  <a
                                    href="/subscription"
                                    className={`${
                                      focus
                                        ? "bg-blue-500 text-white"
                                        : "text-gray-900"
                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                  >
                                    {user.membership === "PREMIUM" ? (
                                      <BadgeCheckIcon
                                        className={classNames(
                                          "mr-2 h-5 w-5",
                                          focus ? "text-white" : "text-blue-500"
                                        )}
                                      />
                                    ) : (
                                      <TagIcon className="mr-2 h-5 w-5" />
                                    )}
                                    <span className="mr-2">
                                      {user.membership === "PREMIUM"
                                        ? "Premium"
                                        : t("Subscription")}
                                    </span>
                                  </a>
                                )}
                              </MenuItem>
                              <MenuItem>
                                {({ focus }) => (
                                  <a
                                    href="/transaction"
                                    className={`${
                                      focus
                                        ? "bg-blue-500 text-white"
                                        : "text-gray-900"
                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                  >
                                    <CashIcon className="mr-2 h-5 w-5" />
                                    <span className="mr-2">
                                      {t("Transactions")}
                                    </span>
                                  </a>
                                )}
                              </MenuItem>
                              {user.membership === "CONSULTANT" && (
                                <MenuItem>
                                  {({ focus }) => (
                                    <a
                                      href="/manage-consultation"
                                      className={`${
                                        focus
                                          ? "bg-blue-500 text-white"
                                          : "text-gray-900"
                                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                      <BriefcaseIcon className="mr-2 h-5 w-5" />
                                      <span className="mr-2 whitespace-nowrap">
                                        {t("Manage Consultation")}
                                      </span>
                                    </a>
                                  )}
                                </MenuItem>
                              )}
                              <MenuItem>
                                {({ focus }) => (
                                  <a
                                    href="/business"
                                    className={`${
                                      focus
                                        ? "bg-blue-500 text-white"
                                        : "text-gray-900"
                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                  >
                                    <OfficeBuildingIcon className="mr-2 h-5 w-5" />
                                    <span className="mr-2 whitespace-nowrap">
                                      {t("Manage Business")}
                                    </span>
                                  </a>
                                )}
                              </MenuItem>
                              <MenuItem>
                                {({ focus }) => (
                                  <a
                                    href="/settings"
                                    className={`${
                                      focus
                                        ? "bg-blue-500 text-white"
                                        : "text-gray-900"
                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                  >
                                    <CogIcon className="mr-2 h-5 w-5" />
                                    <span className="mr-2">
                                      {t("Settings")}
                                    </span>
                                  </a>
                                )}
                              </MenuItem>
                            </div>
                            <MenuItem>
                              {({ focus }) => (
                                <button
                                  onClick={handleLogout}
                                  className={`${
                                    focus
                                      ? "bg-red-500 text-white"
                                      : "text-red-500"
                                  } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                >
                                  <LogoutIcon className="mr-2 w-5 h-5" />
                                  <span className="mr-2">{t("Logout")}</span>
                                </button>
                              )}
                            </MenuItem>
                          </div>
                            </MenuItems>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  </div>
                ) : (
                  <div className="gap-4 flex items-center">
                    {renderLanguageSelect()}

                    <a
                      className="block px-5 py-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-lg"
                      href="/login"
                      onClick={() => {
                        clearAuthData();
                      }}
                    >
                      {t("Log in")}
                    </a>

                    <a
                      className="hidden sm:block px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg"
                      href="/signup"
                    >
                      {t("Sign up")}
                    </a>
                  </div>
                )}

                <button
                  onClick={() => setmenuOpen(!menuOpen)}
                  className="block p-2.5 text-gray-600 transition bg-gray-100 rounded md:hidden hover:text-gray-600/75"
                >
                  <span className="sr-only">Toggle menu</span>
                  <MenuIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
    </div>
  );
};

export default Header;
